<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import type { FormEventHandler } from 'svelte/elements';
  import debounce from 'lodash/debounce';
  import { cx } from '@emotion/css';
  import { ResponsiveVars } from '@twist/core';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import SearchIcon from '$components/Icons/Search.svelte';
  import FontSizeDecorator from '$components/SanityComponents/PortableText/Decorators/FontSizeDecorator.svelte';
  import { flagValue } from '$lib/context/flags';
  import { FEATURE_FLAGS } from '$lib/flags/constants';

  interface Props {
    value?: string;
    debounceChangesForMS?: number;
  }

  let { value = $bindable(''), debounceChangesForMS = 500 }: Props = $props();

  const dispatch = createEventDispatcher();

  const debouncedChange = debounce((newValue: string) => {
    dispatch('change', newValue);
  }, debounceChangesForMS);

  const handleValueChange: FormEventHandler<HTMLInputElement> = (event) => {
    value = event.currentTarget.value;

    debouncedChange(value);
  };

  const responsiveVars = new ResponsiveVars()
    .addFluid('search-font-size', toFluidVarInput(18, 18))
    .addFluid('search-input-py', toFluidVarInput(10, 10))
    .addFluid('search-input-pr', toFluidVarInput(15, 15))
    .addFluid('search-input-pl', toFluidVarInput(45, 45))
    .addFluid('search-icon-size', toFluidVarInput(20, 20))
    .addFluid('search-icon-ml', toFluidVarInput(15, 15))
    .toCSS();

  let brandReskinEnabled = $derived(flagValue(FEATURE_FLAGS.BRAND_RESKIN));
</script>

<div class={cx(responsiveVars, 'search')}>
  {#if brandReskinEnabled}
    <FontSizeDecorator size="normal">
      <input
        {value}
        class="search__input"
        type="text"
        placeholder="Search roles"
        oninput={handleValueChange}
      />
    </FontSizeDecorator>
  {:else}
    <span>
      <input
        {value}
        class="search__input v1"
        type="text"
        placeholder="Search roles"
        oninput={handleValueChange}
      />
    </span>
  {/if}
  <div class="search__icon">
    <SearchIcon />
  </div>
</div>

<style lang="scss">
  .search {
    position: relative;
  }

  .search__input {
    width: 100%;
    font-family: var(--font-family-primary);
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    background: var(--twist-color-gray-0);
    color: var(--twist-color-gray-100);
    padding-block: var(--search-input-py);
    padding-right: var(--search-input-pr);
    padding-left: var(--search-input-pl);
    border: none;
    outline: none;
    border: 1px solid var(--twist-color-gray-100);
    border-radius: 1000px;

    &.v1 {
      font-size: var(--search-font-size);
      font-weight: 600;
      line-height: 140%;
    }

    &::placeholder {
      color: var(--twist-color-gray-100);
    }

    &::selection {
      background: var(--twist-color-gray-100);
      color: var(--twist-color-gray-0);
    }
  }

  .search__icon {
    position: absolute;
    width: var(--search-icon-size);
    height: var(--search-icon-size);
    top: 50%;
    left: var(--search-icon-ml);
    transform: translateY(-50%);
  }
</style>
